/**
 * AssistanceAnimalCardList — ESA / service-animal card editor.
 * Extracted verbatim from Step7PetDetails so the lease wizard and the amendment flow share one form.
 */

import type { AssistanceAnimal } from '../../types/lease-generation';
import { RequiredMark } from '../../pages/lease-wizard/RequiredMark';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { nativeSelectClass } from './form-classes';
import { BLANK_ASSISTANCE_ANIMAL, isAssistanceAnimalComplete } from './pet-validation';

export interface AssistanceAnimalCardListProps {
  animals: AssistanceAnimal[];
  addAssistanceAnimal: (animal: AssistanceAnimal) => void;
  updateAssistanceAnimal: (index: number, update: Partial<AssistanceAnimal>) => void;
  removeAssistanceAnimal: (index: number) => void;
}

export function AssistanceAnimalCardList({
  animals,
  addAssistanceAnimal,
  updateAssistanceAnimal,
  removeAssistanceAnimal,
}: AssistanceAnimalCardListProps) {
  const labelClass = 'block mb-1 text-sm font-medium text-foreground';

  return (
    <>
      {animals.length === 0 && (
        <div className="py-6 text-center bg-accent/50 rounded-lg border border-dashed border-border">
          <p className="text-sm text-muted-foreground">No assistance animals added yet.</p>
        </div>
      )}

      {animals.map((animal, index) => {
        const animalHasError = !isAssistanceAnimalComplete(animal);
        const errorId = `assistance-animal-error-${index}`;
        return (
          <div
            key={index}
            data-testid={`assistance-animal-card-${index}`}
            className="border border-border rounded-lg p-4 bg-card"
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-foreground">
                {animal.type === 'service_animal' ? 'Service Animal' : 'Emotional Support Animal'}
                {animal.name ? `: ${animal.name}` : ` ${index + 1}`}
              </h4>
              <Button
                type="button"
                variant="outline"
                data-testid={`remove-assistance-animal-${index}`}
                onClick={() => removeAssistanceAnimal(index)}
                aria-label={`Remove assistance animal ${index + 1}`}
                className="min-h-[44px] text-destructive border-destructive/30 hover:bg-destructive/5"
              >
                Remove
              </Button>
            </div>

            <div className="flex flex-col gap-3">
              {/* Name, species, breed */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label htmlFor={`assistance-animal-${index}-name`} className={labelClass}>
                    Name <RequiredMark />
                  </label>
                  <Input
                    id={`assistance-animal-${index}-name`}
                    type="text"
                    data-testid={`assistance-animal-${index}-name`}
                    value={animal.name}
                    onChange={e => updateAssistanceAnimal(index, { name: e.target.value })}
                    className="min-h-[44px]"
                    placeholder="e.g. Bella"
                    required
                    aria-required="true"
                    aria-describedby={animalHasError && !animal.name.trim() ? errorId : undefined}
                    aria-invalid={animalHasError && !animal.name.trim() ? true : undefined}
                  />
                </div>
                <div>
                  <label htmlFor={`assistance-animal-${index}-species`} className={labelClass}>
                    Species <RequiredMark />
                  </label>
                  <Input
                    id={`assistance-animal-${index}-species`}
                    type="text"
                    data-testid={`assistance-animal-${index}-species`}
                    value={animal.species}
                    onChange={e => updateAssistanceAnimal(index, { species: e.target.value })}
                    className="min-h-[44px]"
                    placeholder="e.g. Dog"
                    required
                    aria-required="true"
                    aria-describedby={animalHasError && !animal.species.trim() ? errorId : undefined}
                    aria-invalid={animalHasError && !animal.species.trim() ? true : undefined}
                  />
                </div>
                <div>
                  <label htmlFor={`assistance-animal-${index}-breed`} className={labelClass}>
                    Breed / Description <RequiredMark />
                  </label>
                  <Input
                    id={`assistance-animal-${index}-breed`}
                    type="text"
                    data-testid={`assistance-animal-${index}-breed`}
                    value={animal.breed}
                    onChange={e => updateAssistanceAnimal(index, { breed: e.target.value })}
                    className="min-h-[44px]"
                    placeholder="e.g. Labrador Retriever"
                    required
                    aria-required="true"
                    aria-describedby={animalHasError && !animal.breed.trim() ? errorId : undefined}
                    aria-invalid={animalHasError && !animal.breed.trim() ? true : undefined}
                  />
                </div>
              </div>

              {/* Type selector */}
              <div>
                <label htmlFor={`assistance-animal-${index}-type`} className={labelClass}>
                  Animal Type <RequiredMark />
                </label>
                <select
                  id={`assistance-animal-${index}-type`}
                  data-testid={`assistance-animal-${index}-type`}
                  value={animal.type}
                  onChange={e => updateAssistanceAnimal(index, { type: e.target.value as AssistanceAnimal['type'] })}
                  className={nativeSelectClass}
                  required
                  aria-required="true"
                >
                  <option value="esa">Emotional Support Animal</option>
                  <option value="service_animal">Service Animal</option>
                </select>
              </div>

              {/* ESA-specific fields */}
              {animal.type === 'esa' && (
                <div className="flex flex-col gap-3 pl-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor={`assistance-animal-${index}-provider-name`} className={labelClass}>
                        Provider Name <RequiredMark />
                      </label>
                      <Input
                        id={`assistance-animal-${index}-provider-name`}
                        type="text"
                        data-testid={`assistance-animal-${index}-provider-name`}
                        value={animal.providerName ?? ''}
                        onChange={e => updateAssistanceAnimal(index, { providerName: e.target.value })}
                        className="min-h-[44px]"
                        placeholder="e.g. Dr. Jane Smith"
                        required
                        aria-required="true"
                        aria-describedby={animalHasError && !animal.providerName?.trim() ? errorId : undefined}
                        aria-invalid={animalHasError && !animal.providerName?.trim() ? true : undefined}
                      />
                    </div>
                    <div>
                      <label htmlFor={`assistance-animal-${index}-provider-license`} className={labelClass}>
                        Provider License Number <RequiredMark />
                      </label>
                      <Input
                        id={`assistance-animal-${index}-provider-license`}
                        type="text"
                        data-testid={`assistance-animal-${index}-provider-license`}
                        value={animal.providerLicense ?? ''}
                        onChange={e => updateAssistanceAnimal(index, { providerLicense: e.target.value })}
                        className="min-h-[44px]"
                        placeholder="e.g. LIC-12345"
                        required
                        aria-required="true"
                        aria-describedby={animalHasError && !animal.providerLicense?.trim() ? errorId : undefined}
                        aria-invalid={animalHasError && !animal.providerLicense?.trim() ? true : undefined}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor={`assistance-animal-${index}-documentation-date`} className={labelClass}>
                      Documentation Date <RequiredMark />
                    </label>
                    <Input
                      id={`assistance-animal-${index}-documentation-date`}
                      type="date"
                      data-testid={`assistance-animal-${index}-documentation-date`}
                      value={animal.documentationDate ?? ''}
                      onChange={e => updateAssistanceAnimal(index, { documentationDate: e.target.value })}
                      className="min-h-[44px]"
                      required
                      aria-required="true"
                      aria-describedby={animalHasError && !animal.documentationDate ? errorId : undefined}
                      aria-invalid={animalHasError && !animal.documentationDate ? true : undefined}
                    />
                  </div>
                </div>
              )}

              {/* Service animal-specific fields */}
              {animal.type === 'service_animal' && (
                <div>
                  <label htmlFor={`assistance-animal-${index}-task-description`} className={labelClass}>
                    Task / Work Description <RequiredMark />
                  </label>
                  <Textarea
                    id={`assistance-animal-${index}-task-description`}
                    data-testid={`assistance-animal-${index}-task-description`}
                    value={animal.taskDescription ?? ''}
                    onChange={e => updateAssistanceAnimal(index, { taskDescription: e.target.value })}
                    className="min-h-[80px] resize-y"
                    placeholder="Describe the specific task or work the animal is trained to perform for the person with a disability"
                    required
                    aria-required="true"
                    aria-describedby={animalHasError && !animal.taskDescription?.trim() ? errorId : undefined}
                    aria-invalid={animalHasError && !animal.taskDescription?.trim() ? true : undefined}
                  />
                </div>
              )}

              {animalHasError && (
                <p
                  id={errorId}
                  data-testid={`assistance-animal-error-${index}`}
                  role="alert"
                  className="text-xs text-warning bg-warning/10 border border-warning/20 rounded px-3 py-2"
                >
                  Please complete all required fields for this assistance animal.
                </p>
              )}
            </div>
          </div>
        );
      })}

      <Button
        type="button"
        variant="outline"
        data-testid="add-assistance-animal-button"
        onClick={() => addAssistanceAnimal({ ...BLANK_ASSISTANCE_ANIMAL })}
        aria-label="Add assistance animal"
        className="w-full min-h-[44px] border-dashed text-primary border-primary/40 hover:bg-primary/5"
      >
        + Add Assistance Animal
      </Button>
    </>
  );
}
